import React, { useState, useEffect } from 'react';
import { nfConfirm } from '../components/confirm';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { EditIcon, KeyIcon, TrashIcon } from '../components/ActionIcons';
import ClientMessengerSearch from '../components/ClientMessengerSearch';

const roleOrder = ['owner', 'admin', 'florist', 'packer', 'courier'];

// Только фирменная палитра NovaFlo (сосна, терракота, охра, песок) — без синего и фиолетового
const roleBadge = {
  owner: { label: 'Владелец', bg: '#1C3D2E', color: '#F4F4EF' },
  admin: { label: 'Админ', bg: '#FFE7DD', color: '#B0603C' },
  florist: { label: 'Флорист', bg: '#DFF2E8', color: '#1C3D2E' },
  packer: { label: 'Упаковщик', bg: '#F9EBCF', color: '#7E5D00' },
  courier: { label: 'Курьер', bg: '#EBEDEC', color: '#5E6561' },
};

const CHANNEL_LABELS = { telegram: 'Telegram', max: 'Max' };

// Итог отправки логина и временного пароля: { telegram: true, max: false }
function deliveryToast(delivery, showToast, prefix) {
  const ok = Object.keys(delivery || {}).filter((k) => delivery[k]).map((k) => CHANNEL_LABELS[k]);
  const failed = Object.keys(delivery || {}).filter((k) => !delivery[k]).map((k) => CHANNEL_LABELS[k]);
  if (ok.length === 0) {
    showToast(`${prefix}, но сообщение с паролем не дошло (${failed.join(', ')}). Пусть сотрудник нажмёт «Старт» в боте магазина, затем сбросьте пароль 🔑`, 'error');
    return;
  }
  showToast(`${prefix}. Логин и временный пароль отправлены в ${ok.join(' и ')}`
    + (failed.length ? `; в ${failed.join(', ')} не дошло` : ''));
}

const emptyForm = {
  name: '', role: 'florist', secondary_role: null, is_active: true, login: '', telegram_id: '', max_id: '',
};

const roleLabelMap = {
  owner: 'владельца',
  admin: 'администратора',
  florist: 'флориста',
  packer: 'упаковщика',
  courier: 'курьера',
};

function EmployeesPage({ showToast }) {
  const { user } = useAuth();
  // Управление учётками (создание, сброс паролей, роли) — только владелец;
  // админу список нужен для назначения флористов/курьеров на заказы.
  const isOwner = user?.role === 'owner';
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  // Кто в базе клиентов под введёнными ID: { telegram: {found, name}, max: {...} }
  const [clientLookup, setClientLookup] = useState({});

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (!showModal || !isOwner) return undefined;
    // В Telegram можно набирать @username — в проверку «есть в базе» идут только цифры (ID)
    const tg = /^\d+$/.test((formData.telegram_id || '').trim()) ? formData.telegram_id.trim() : '';
    const mx = /^\d+$/.test((formData.max_id || '').trim()) ? formData.max_id.trim() : '';
    if (!tg && !mx) {
      setClientLookup({});
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        setClientLookup(await api.lookupEmployeeClient(tg, mx));
      } catch {
        setClientLookup({});
      }
    }, 400);
    return () => clearTimeout(t);
  }, [showModal, isOwner, formData.telegram_id, formData.max_id]);

  const loadEmployees = async () => {
    try {
      const data = await api.getAllEmployees();
      // Sort: by role order, then by name
      data.sort((a, b) => {
        const ra = roleOrder.indexOf(a.role);
        const rb = roleOrder.indexOf(b.role);
        if (ra !== rb) return ra - rb;
        return a.name.localeCompare(b.name, 'ru');
      });
      setEmployees(data);
    } catch (error) {
      showToast('Ошибка загрузки сотрудников', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const activeOwnerCount = employees.filter(e => e.role === 'owner' && e.is_active !== false).length;
  // Учётки для входа без мессенджера: им не работают «Забыли пароль?» и сброс 🔑
  const noMessengerCount = employees.filter(
    e => e.login && e.is_active !== false && !e.telegram_id && !e.max_id
  ).length;

  const openCreateModal = () => {
    setEditingEmployee(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      role: employee.role,
      secondary_role: employee.secondary_role || null,
      is_active: employee.is_active !== false,
      login: employee.login || '',
      telegram_id: employee.telegram_id || '',
      max_id: employee.max_id || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showToast('Введите имя сотрудника', 'error');
      return;
    }

    if (formData.telegram_id && !/^\d+$/.test(formData.telegram_id)) {
      showToast('Telegram: выберите человека из подсказок или впишите числовой ID', 'error');
      return;
    }
    if (formData.max_id && !/^\d+$/.test(formData.max_id)) {
      showToast('Max ID — только цифры', 'error');
      return;
    }

    // Новая учётка для входа (создание или логин впервые) — нужен мессенджер:
    // туда придут логин и временный пароль
    const getsLogin = formData.login.trim() && !(editingEmployee && editingEmployee.login);
    if (getsLogin && !formData.telegram_id.trim() && !formData.max_id.trim()) {
      showToast('Укажите Telegram ID или Max ID — туда придут логин и пароль', 'error');
      return;
    }

    try {
      const submitData = { ...formData };
      // secondary_role отправляем как есть (null или 'packer')
      // НЕ удаляем поле, чтобы бэкенд знал об изменении

      if (editingEmployee) {
        // ID мессенджеров — только если их меняли: у старых учёток их ещё нет, и
        // правка имени или выключение сотрудника не должны упираться в проверку
        if (submitData.telegram_id === (editingEmployee.telegram_id || '')) delete submitData.telegram_id;
        if (submitData.max_id === (editingEmployee.max_id || '')) delete submitData.max_id;
        const updated = await api.updateEmployee(editingEmployee.id, submitData);
        if (updated.password_delivery) {
          deliveryToast(updated.password_delivery, showToast, 'Сотрудник обновлён');
        } else {
          showToast('Сотрудник обновлен');
        }
      } else {
        const created = await api.createEmployee(submitData);
        if (created.password_delivery) {
          deliveryToast(created.password_delivery, showToast, 'Сотрудник создан');
        } else {
          showToast('Сотрудник создан');
        }
      }
      setShowModal(false);
      loadEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const handleResetPassword = async (employee) => {
    const where = [employee.telegram_id && 'Telegram', employee.max_id && 'Max'].filter(Boolean).join(' и ');
    if (!where) {
      showToast('Сначала укажите в карточке Telegram ID или Max ID — новый пароль приходит туда', 'error');
      return;
    }
    if (!(await nfConfirm(
      `Сбросить пароль: ${employee.name}? Старый перестанет работать сразу, новый временный придёт в ${where}.`,
      { confirmText: 'Сбросить' }
    ))) return;
    try {
      const res = await api.resetEmployeePassword(employee.id);
      deliveryToast(res.password_delivery, showToast, 'Пароль сброшен');
    } catch (error) {
      showToast(error.message || 'Не удалось сбросить пароль', 'error');
    }
  };

  const handleDelete = async (id, employee) => {
    if (user && user.id === id) {
      showToast('Нельзя удалить самого себя', 'error');
      return;
    }
    // У кого есть история (заказы, сборки, статусы), сервер скрывает запись, а не стирает:
    // из списка пропадает, вход закрыт везде, имя в старых заказах остаётся
    if (!(await nfConfirm(
      `Удалить ${roleLabelMap[employee.role] || 'сотрудника'} «${employee.name}»? Он сразу выйдет из панели на всех устройствах и больше не сможет войти. В старых заказах его имя останется.`,
      { confirmText: 'Удалить' },
    ))) return;

    try {
      await api.deleteEmployee(id);
      showToast('Сотрудник удалён — вход закрыт на всех устройствах');
      loadEmployees();
    } catch (error) {
      showToast(error.message || 'Ошибка удаления', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const modalTitle = editingEmployee
    ? `Редактировать ${roleLabelMap[formData.role] || 'сотрудника'}`
    : 'Новый сотрудник';

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title">Сотрудники</h1>
        {isOwner && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            + Добавить сотрудника
          </button>
        )}
      </div>

      {!isOwner && (
        <p style={{ margin: '0 0 16px', color: '#888', fontSize: 13 }}>
          Создание учёток, сброс паролей и смена ролей доступны только владельцу.
        </p>
      )}

      {isOwner && noMessengerCount > 0 && (
        <p style={{ margin: '0 0 16px', color: '#B26A00', fontSize: 13 }}>
          {`У ${noMessengerCount} ${noMessengerCount % 10 === 1 && noMessengerCount % 100 !== 11 ? 'учётки' : 'учёток'} не указан мессенджер — `}
          без него не работают «Забыли пароль?» и сброс пароля. Укажите его в карточке сотрудника, в том числе себе.
        </p>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Имя</th>
              <th>Роль</th>
              <th>Логин</th>
              <th>Мессенджер</th>
              <th>Статус</th>
              {isOwner && <th>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan={isOwner ? 6 : 5} style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                  Сотрудников пока нет
                </td>
              </tr>
            ) : (
              employees.map(employee => {
                const badge = roleBadge[employee.role] || { label: employee.role, bg: '#F4F4EF', color: '#333' };
                const isOnlyOwner = employee.role === 'owner' && activeOwnerCount <= 1;
                const isSelf = user && user.id === employee.id;
                // Правила удаления: системную учётку admin и себя — нельзя, последнего владельца — нельзя
                const isSystemAdmin = (employee.login || '').trim().toLowerCase() === 'admin';

                return (
                  <tr key={employee.id}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{employee.name}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          '--ch': '10px',
                          fontSize: 12,
                          fontWeight: 600,
                          background: badge.bg,
                          color: badge.color
                        }}>
                          {badge.label}
                        </span>
                        {employee.secondary_role === 'packer' && (
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            '--ch': '10px',
                            fontSize: 12,
                            fontWeight: 600,
                            background: roleBadge.packer.bg,
                            color: roleBadge.packer.color
                          }}>
                            +{roleBadge.packer.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px', color: employee.login ? '#333' : '#999' }}>
                      {employee.login || '---'}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#666' }}>
                      {[employee.telegram_id && 'Telegram', employee.max_id && 'Max'].filter(Boolean).join(', ')
                        || (employee.login ? <span style={{ color: '#B26A00' }}>не указан</span> : '—')}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span className={`badge badge-${employee.is_active !== false ? 'active' : 'inactive'}`}>
                        {employee.is_active !== false ? 'Активен' : 'Неактивен'}
                      </span>
                    </td>
                    {isOwner && (
                      <td style={{ padding: '8px 12px' }}>
                        <div className="table-actions">
                          <button
                            type="button" className="btn-icon"
                            title="Изменить" aria-label="Изменить"
                            onClick={() => openEditModal(employee)}
                          >
                            <EditIcon />
                          </button>
                          {/* Себе — через «Забыли пароль?» на экране входа: там старый
                              пароль работает, пока не использован временный */}
                          {employee.login && !isSelf && (
                            <button
                              type="button" className="btn-icon"
                              title="Сбросить пароль" aria-label="Сбросить пароль"
                              onClick={() => handleResetPassword(employee)}
                            >
                              <KeyIcon />
                            </button>
                          )}
                          {!isSelf && !isOnlyOwner && !isSystemAdmin && (
                            <button
                              type="button" className="btn-icon is-danger"
                              title="Удалить" aria-label="Удалить"
                              onClick={() => handleDelete(employee.id, employee)}
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal
          title={modalTitle}
          onClose={() => setShowModal(false)}
          confirmOverlayClose
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Отмена
              </button>
              <button className="btn btn-primary" onClick={handleSubmit}>
                {editingEmployee ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Имя</label>
            <input
              type="text"
              className="form-input"
              placeholder="Введите имя"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Роль</label>
            <select
              className="form-input"
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value, secondary_role: e.target.value !== 'florist' ? null : prev.secondary_role }))}
            >
              {roleOrder.map(r => (
                <option key={r} value={r}>{roleBadge[r].label}</option>
              ))}
            </select>
            {formData.role === 'owner' && (
              <small style={{ display: 'block', marginTop: 4, color: '#666', fontSize: 12 }}>
                Владелец видит финансовую статистику и управляет учётками сотрудников
              </small>
            )}
          </div>

          {formData.role === 'florist' && (
            <div className="form-group">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.secondary_role === 'packer'}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondary_role: e.target.checked ? 'packer' : null }))}
                />
                Также является упаковщиком (двойная роль)
              </label>
              <small style={{ display: 'block', marginTop: 4, color: '#666', fontSize: 12 }}>
                Флорист с ролью упаковщика может сам упаковывать букеты, минуя этап передачи упаковщику
              </small>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Логин (для входа в панель)</label>
            <input
              type="text"
              className="form-input"
              placeholder="Введите логин"
              value={formData.login}
              disabled={(editingEmployee?.login || '').trim().toLowerCase() === 'admin'}
              title={(editingEmployee?.login || '').trim().toLowerCase() === 'admin' ? 'Логин системной учётки admin не меняется' : undefined}
              onChange={(e) => setFormData(prev => ({ ...prev, login: e.target.value }))}
            />
          </div>

          {/* Пароль владелец не задаёт: логин и временный пароль бот магазина
              присылает сотруднику в Telegram/Max, первый вход — смена на свой */}
          {/* Telegram — одно поле: @username, имя или ID; по буквам подсказываем клиентов
              с Telegram (человек хоть раз нажимал «Старт» в боте), выбор ставит его ID.
              Max — только числовой ID (username у Max нет). */}
          {(() => {
            const tg = formData.telegram_id || '';
            const tgIsId = /^\d+$/.test(tg);
            const found = clientLookup.telegram;
            return (
              <div className="form-group">
                <label className="form-label">Telegram</label>
                <ClientMessengerSearch
                  platform="telegram"
                  searchDigits={false}
                  placeholder="@username, имя или ID"
                  value={tg}
                  onChange={(v) => setFormData(prev => ({ ...prev, telegram_id: v.trim() }))}
                  onPick={(u) => setFormData(prev => ({ ...prev, telegram_id: u.telegram_id || prev.telegram_id }))}
                />
                {tg && !tgIsId && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#909894' }}>
                    Выберите человека из подсказок — в поле встанет его Telegram ID
                  </small>
                )}
                {tgIsId && found && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: 12, color: found.found ? 'var(--primary-color)' : 'var(--danger-color)' }}>
                    {found.found ? `✓ ${found.name}` : 'Нет в базе клиентов — пусть сотрудник откроет бота магазина и нажмёт «Старт»'}
                  </small>
                )}
              </div>
            );
          })()}
          {(() => {
            const mx = formData.max_id || '';
            const found = clientLookup.max;
            return (
              <div className="form-group">
                <label className="form-label">Max ID</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  placeholder="Только цифры"
                  value={mx}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_id: e.target.value.trim() }))}
                />
                {mx && !/^\d+$/.test(mx) && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--danger-color)' }}>
                    Только цифры
                  </small>
                )}
                {mx && /^\d+$/.test(mx) && found && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: 12, color: found.found ? 'var(--primary-color)' : 'var(--danger-color)' }}>
                    {found.found ? `✓ ${found.name}` : 'Нет в базе клиентов — пусть сотрудник откроет бота магазина и нажмёт «Старт»'}
                  </small>
                )}
              </div>
            );
          })()}
          <small style={{ display: 'block', margin: '-8px 0 16px', color: '#666', fontSize: 12 }}>
            {formData.login.trim() ? 'Нужен хотя бы один. ' : ''}
            ID есть в разделе «Клиенты» и в профиле приложения у самого сотрудника.
            {formData.login.trim() && !(editingEmployee && editingEmployee.login)
              && ' Логин и временный пароль придут туда, при первом входе сотрудник задаст свой.'}
          </small>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.is_active}
                disabled={(editingEmployee?.login || '').trim().toLowerCase() === 'admin'}
                title={(editingEmployee?.login || '').trim().toLowerCase() === 'admin' ? 'Системную учётку admin нельзя отключить' : undefined}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              Активен (доступен для выбора)
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default EmployeesPage;
